/**
 * HomePage コンポーネント用テキストデータ
 * 会員情報表示に関連するテキスト管理
 */

import { convertToDisplayName } from '../utils/paymentMethodUtils';

export const homePageTexts = {
  ja: {
    // ページタイトル・メッセージ
    page: {
      title: 'ホーム画面',
      loadingAuth: '認証状態を確認中...',
      loadingMember: '会員情報を読み込み中...',
      redirectingLogin: 'ログイン画面にリダイレクト中...',
      memberDataError: '会員情報の取得に失敗しました',
      memberDataNotFound: '会員データが見つかりません。お使いのメールアドレス（{email}）で登録された会員情報がない可能性があります。',
      memberUpdateFailed: '会員情報の更新に失敗しました',
      logoutFailed: 'ログアウトに失敗しました'
    },

    // カードタイトル
    cardTitles: {
      accountInfo: 'アカウント情報',
      memberBasicInfo: '会員基本情報',
      addressInfo: '住所情報',
      storeContractInfo: '店舗・契約情報',
      emergencyContact: '緊急連絡先',
      registrationInfo: '登録情報'
    },

    // フィールドラベル
    labels: {
      memberId: '会員ID',
      email: 'メールアドレス',
      emailVerification: 'メール認証',
      fullName: 'お名前',
      fullNameKana: 'フリガナ',
      phoneNumber: '電話番号',
      birthDate: '生年月日',
      gender: '性別',
      postalCode: '郵便番号',
      prefecture: '都道府県',
      city: '市区町村',
      streetAddress: '町域・番地',
      buildingRoom: '建物・部屋番号',
      address: '住所',
      storeUsage: '利用店舗',
      joinDate: '入会日',
      paymentMethod: '支払い方法',
      additionalStore: '追加利用店舗',
      shoeLocker: 'シューズロッカー',
      emergencyContactName: '連絡先氏名',
      emergencyContactPhone: '連絡先電話'
    },

    // ステータス表示
    status: {
      verified: '✓ 認証済み',
      unverified: '⚠ 未認証',
      notSet: '未設定',
      noSelection: '選択しない'
    },

    // 性別の表示マッピング
    gender: {
      'MALE': '男性',
      'FEMALE': '女性',
      'OTHER': 'その他',
      'PREFER_NOT_TO_SAY': '回答しない',
      '男性': '男性',
      '女性': '女性',
      'その他': 'その他'
    },

    // シューズロッカー申込状況の表示マッピング
    shoeLocker: {
      'APPLIED': '申込済み',
      'NOT_APPLIED': '申込なし',
      'WAITING': '待機中',
      'APPROVED': '承認済み',
      'REJECTED': '却下',
      '申込済み': '申込済み',
      '申込なし': '申込なし',
      '希望する': '申込済み',
      '希望しない': '申込なし'
    },

    // 店舗名マッピング（フォールバック用）
    storeNames: {
      'SHIBUYA': '渋谷店',
      'SHINJUKU': '新宿店',
      'IKEBUKURO': '池袋店',
      'UENO': '上野店',
      'OKACHIMACHI': '御徒町店'
    },

    manuals: {
      title: 'マニュアル・ガイド',
      description: '各種マニュアルをご確認いただけます。',
      storeLayout: '店舗レイアウト',
      reservationManual: '予約マニュアル',
      robotManual: 'ロボットマニュアル',
      smartLockInstallManual: 'スマートロックインストールマニュアル',
      preparingMessage: 'このマニュアルは現在準備中です。しばらくお待ちください。'
    },

    // ボタンテキスト
    buttons: {
      retry: '再試行',
      research: '再検索',
      profileEdit: 'プロフィール編集',
      storeHistory: '所属店舗履歴',
      updateInfo: '情報更新',
      logout: 'ログアウト',
      facilityReservation: '施設予約',
      usageHistory: '利用履歴',
      storeLayout: '店舗レイアウト',
      reservationManual: '予約マニュアル',
      robotManual: 'ロボットマニュアル',
      smartLockInstallManual: 'スマートロック設定マニュアル'
    },

    // アラート・通知メッセージ
    alerts: {
      profileEditComingSoon: 'プロフィール編集機能は準備中です',
      settingsComingSoon: '設定機能は準備中です',
      reservationComingSoon: '予約システムは準備中です',
      usageHistoryComingSoon: '利用履歴機能は準備中です'
    },

    // サービス情報
    serviceInfo: {
      title: '卓トレサービス',
      description: '卓トレは無人管理施設です。会員様お一人お一人のご協力のもと、快適な練習環境を提供しております。ご利用ありがとうございます。'
    },

    // デバッグ情報
    debug: {
      title: 'デバッグ情報 (開発環境のみ)',
      note: '開発環境でのみ表示されます'
    }
  },

  en: {
    // 英語版（将来の国際化対応用）
    page: {
      title: 'Home',
      loadingAuth: 'Checking authentication status...',
      loadingMember: 'Loading member information...',
      redirectingLogin: 'Redirecting to login...',
      memberDataError: 'Failed to retrieve member information',
      memberDataNotFound: 'Member data not found for email: {email}',
      memberUpdateFailed: 'Failed to update member information',
      logoutFailed: 'Logout failed'
    },

    cardTitles: {
      accountInfo: 'Account Information',
      memberBasicInfo: 'Member Basic Information',
      addressInfo: 'Address Information',
      storeContractInfo: 'Store & Contract Information',
      emergencyContact: 'Emergency Contact',
      registrationInfo: 'Registration Information'
    },

    labels: {
      memberId: 'Member ID',
      email: 'Email Address',
      emailVerification: 'Email Verification',
      fullName: 'Full Name',
      fullNameKana: 'Full Name (Kana)',
      phoneNumber: 'Phone Number',
      birthDate: 'Date of Birth',
      gender: 'Gender',
      postalCode: 'Postal Code',
      prefecture: 'Prefecture',
      city: 'City',
      streetAddress: 'Street Address',
      buildingRoom: 'Building/Room Number',
      address: 'Address',
      storeUsage: 'Store Usage',
      joinDate: 'Join Date',
      paymentMethod: 'Payment Method',
      additionalStore: 'Additional Store',
      shoeLocker: 'Shoe Locker',
      emergencyContactName: 'Emergency Contact Name',
      emergencyContactPhone: 'Emergency Contact Phone'
    },

    status: {
      verified: '✓ Verified',
      unverified: '⚠ Unverified',
      notSet: 'Not Set',
      noSelection: 'None Selected'
    }
  }
};

/**
 * 指定言語のテキストデータを取得
 * @param {string} language - 言語コード ('ja' | 'en')
 * @returns {Object} テキストデータ
 */
export const getHomePageTexts = (language = 'ja') => {
  return homePageTexts[language] || homePageTexts.ja;
};

/**
 * ネストされたパスからテキストを取得するヘルパー関数
 * @param {string} path - ドット記法のパス（例: 'labels.fullName'）
 * @param {string} language - 言語コード
 * @param {string} fallback - フォールバック文字列
 * @returns {string} 取得されたテキスト
 */
export const getHomePageText = (path, language = 'ja', fallback = '') => {
  const texts = getHomePageTexts(language);
  const keys = path.split('.');
  
  let result = texts;
  for (const key of keys) {
    if (result && result[key] !== undefined) {
      result = result[key];
    } else {
      return fallback;
    }
  }
  
  return result || fallback;
};

/**
 * 値変換ヘルパー関数群
 */
export const homePageFormatters = {
  /**
   * 性別を日本語表示に変換
   * @param {string} gender - 性別値
   * @param {string} language - 言語コード
   * @returns {string} 変換された性別表示
   */
  formatGender: (gender, language = 'ja') => {
    const texts = getHomePageTexts(language);
    return texts.gender[gender] || getHomePageText('status.notSet', language);
  },

  /**
   * 支払い方法を日本語表示に変換
   * @param {string} paymentMethod - 支払い方法値
   * @param {string} language - 言語コード
   * @returns {string} 変換された支払い方法表示
   */
  formatPaymentMethod: (paymentMethod, language = 'ja') => {
    const displayName = convertToDisplayName(paymentMethod);
    return displayName || getHomePageText('status.notSet', language);
  },

  /**
   * シューズロッカー申込状況を日本語表示に変換
   * @param {string} status - 申込状況値
   * @param {string} language - 言語コード
   * @returns {string} 変換された申込状況表示
   */
  formatShoesLockerStatus: (status, language = 'ja') => {
    const texts = getHomePageTexts(language);
    
    // Boolean値を文字列に変換
    let statusKey = status;
    if (typeof status === 'boolean') {
      statusKey = status ? '申込済み' : '申込なし';
      return statusKey;
    }
    
    return texts.shoeLocker[status] || getHomePageText('status.notSet', language);
  },

  /**
   * 店舗名を取得（storeUtilsのフォールバック付き）
   * @param {string} storeCode - 店舗コード
   * @param {string} language - 言語コード
   * @returns {string} 店舗名
   */
  formatStoreName: (storeCode, language = 'ja') => {
    const texts = getHomePageTexts(language);
    return texts.storeNames[storeCode] || storeCode || getHomePageText('status.notSet', language);
  },

  /**
   * 電話番号をマスキング
   * @param {string} phoneNumber - 電話番号
   * @param {string} language - 言語コード
   * @returns {string} マスキングされた電話番号
   */
  maskPhoneNumber: (phoneNumber, language = 'ja') => {
    if (!phoneNumber) return getHomePageText('status.notSet', language);
    const cleaned = phoneNumber.replace(/[^\d-]/g, '');
    if (cleaned.length >= 8) {
      return cleaned.slice(0, -4) + '****';
    }
    return phoneNumber;
  },

  /**
   * 日付をフォーマット
   * @param {string} dateString - 日付文字列
   * @param {string} language - 言語コード
   * @returns {string} フォーマットされた日付
   */
  formatDate: (dateString, language = 'ja') => {
    if (!dateString) return getHomePageText('status.notSet', language);
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(language === 'ja' ? 'ja-JP' : 'en-US');
    } catch {
      return dateString;
    }
  },

  /**
   * 住所を結合して表示
   * @param {Object} memberData - 会員データ
   * @param {string} language - 言語コード
   * @returns {string} フォーマットされた住所
   */
  formatAddress: (memberData, language = 'ja') => {
    if (!memberData) return getHomePageText('status.notSet', language);
    
    const parts = [
      memberData.postalCode && `〒${memberData.postalCode}`,
      memberData.prefecture,
      memberData.city,
      memberData.town || memberData.address,
      memberData.building || memberData.buildingRoom
    ].filter(Boolean);
    
    return parts.length > 0 ? parts.join(' ') : getHomePageText('status.notSet', language);
  },

  /**
   * 動的メッセージ生成（プレースホルダー置換）
   * @param {string} message - メッセージテンプレート
   * @param {Object} replacements - 置換用オブジェクト
   * @returns {string} 置換後メッセージ
   */
  formatMessage: (message, replacements = {}) => {
    return Object.keys(replacements).reduce((msg, key) => {
      return msg.replace(new RegExp(`{${key}}`, 'g'), replacements[key]);
    }, message);
  }
};

// デフォルトエクスポート
export default homePageTexts;